import type { AgentAdapterType, CompanyRole, JoinRequest, PermissionKey } from "@ligament/shared";
import { api } from "./client";

type InviteSummary = {
  id: string;
  companyId: string | null;
  inviteType: "company_join" | "bootstrap_ceo";
  allowedJoinTypes: "human" | "agent" | "both";
  expiresAt: string;
  onboardingPath?: string;
  onboardingUrl?: string;
  onboardingTextPath?: string;
  onboardingTextUrl?: string;
  skillIndexPath?: string;
  skillIndexUrl?: string;
  inviteMessage?: string | null;
};

type AcceptInviteInput =
  | { requestType: "human" }
  | {
    requestType: "agent";
    agentName: string;
    adapterType?: AgentAdapterType;
    capabilities?: string | null;
    agentDefaultsPayload?: Record<string, unknown> | null;
  };

type AgentJoinRequestAccepted = JoinRequest & {
  claimSecret: string;
  claimApiKeyPath: string;
  onboarding?: Record<string, unknown>;
  diagnostics?: Array<{
    code: string;
    level: "info" | "warn";
    message: string;
    hint?: string;
  }>;
};

type InviteOnboardingManifest = {
  invite: InviteSummary;
  onboarding: {
    inviteMessage?: string | null;
    connectivity?: {
      guidance?: string;
      connectionCandidates?: string[];
      testResolutionEndpoint?: {
        method?: string;
        path?: string;
        url?: string;
      };
    };
    textInstructions?: {
      url?: string;
    };
  };
};

type BoardClaimStatus = {
  status: "available" | "claimed" | "expired";
  requiresSignIn: boolean;
  expiresAt: string | null;
  claimedByUserId: string | null;
};

type CompanyInviteCreated = {
  id: string;
  token: string;
  inviteUrl: string;
  expiresAt: string;
  allowedJoinTypes: "human" | "agent" | "both";
  onboardingTextPath?: string;
  onboardingTextUrl?: string;
  inviteMessage?: string | null;
};

export const accessApi = {
  createPracticeInvite: (
    companyId: string,
    input: {
      allowedJoinTypes?: "human" | "agent" | "both";
      defaultsPayload?: Record<string, unknown> | null;
      agentMessage?: string | null;
    } = {},
  ) =>
    api.post<CompanyInviteCreated>(`/companies/${companyId}/invites`, input),

  createGatewayInvitePrompt: (
    companyId: string,
    input: {
      agentMessage?: string | null;
    } = {},
  ) =>
    api.post<CompanyInviteCreated>(
      `/companies/${companyId}/gateway/invite-prompt`,
      input,
    ),

  getInvite: (token: string) => api.get<InviteSummary>(`/invites/${token}`),
  getInviteOnboarding: (token: string) =>
    api.get<InviteOnboardingManifest>(`/invites/${token}/onboarding`),

  acceptInvite: (token: string, input: AcceptInviteInput) =>
    api.post<AgentJoinRequestAccepted | JoinRequest | { bootstrapAccepted: true; userId: string }>(
      `/invites/${token}/accept`,
      input,
    ),

  listJoinRequests: (companyId: string, status: "pending_approval" | "approved" | "rejected" = "pending_approval") =>
    api.get<JoinRequest[]>(`/companies/${companyId}/join-requests?status=${status}`),

  approveJoinRequest: (companyId: string, requestId: string) =>
    api.post<JoinRequest>(`/companies/${companyId}/join-requests/${requestId}/approve`, {}),

  rejectJoinRequest: (companyId: string, requestId: string) =>
    api.post<JoinRequest>(`/companies/${companyId}/join-requests/${requestId}/reject`, {}),

  claimJoinRequestApiKey: (requestId: string, claimSecret: string) =>
    api.post<{ keyId: string; token: string; agentId: string; createdAt: string }>(
      `/join-requests/${requestId}/claim-api-key`,
      { claimSecret },
    ),

  getBoardClaimStatus: (token: string, code: string) =>
    api.get<BoardClaimStatus>(`/board-claim/${token}?code=${encodeURIComponent(code)}`),

  claimBoard: (token: string, code: string) =>
    api.post<{ claimed: true; userId: string }>(`/board-claim/${token}/claim`, { code }),

  // ── Roles (RBAC) ───────────────────────────────────────────────
  listRoles: (companyId: string) =>
    api.get<CompanyRole[]>(`/companies/${companyId}/roles`),

  listPermissionKeys: (companyId: string) =>
    api.get<{ permissionKeys: PermissionKey[] }>(
      `/companies/${companyId}/roles/permission-keys`,
    ),

  createRole: (
    companyId: string,
    input: { name: string; description?: string | null; permissionKeys: PermissionKey[] },
  ) => api.post<CompanyRole>(`/companies/${companyId}/roles`, input),

  updateRole: (
    companyId: string,
    roleId: string,
    input: { name?: string; description?: string | null; permissionKeys?: PermissionKey[] },
  ) => api.put<CompanyRole>(`/companies/${companyId}/roles/${roleId}`, input),

  deleteRole: (companyId: string, roleId: string) =>
    api.delete<{ deleted: true }>(`/companies/${companyId}/roles/${roleId}`),

  assignRole: (
    companyId: string,
    roleId: string,
    input: { principalType: "user" | "agent"; principalId: string },
  ) =>
    api.post<{ assigned: number }>(
      `/companies/${companyId}/roles/${roleId}/assign`,
      input,
    ),

  // ── Instance-level user access (instance-admin only) ───────────────
  // These hit the Express-bridged /api/admin/users/:id/* routes, each guarded
  // by assertInstanceAdmin server-side (a non-instance-admin caller gets 403).
  getUserCompanyAccess: (userId: string) =>
    api.get<Array<{ id: string; companyId: string; status?: string; membershipRole?: string | null }>>(
      `/admin/users/${userId}/company-access`,
    ),

  setUserCompanyAccess: (userId: string, companyIds: string[]) =>
    api.put<Array<{ id: string; companyId: string }>>(
      `/admin/users/${userId}/company-access`,
      { companyIds },
    ),

  promoteInstanceAdmin: (userId: string) =>
    api.post<{ id: string; userId: string; role: string }>(
      `/admin/users/${userId}/promote-instance-admin`,
      {},
    ),

  demoteInstanceAdmin: (userId: string) =>
    api.post<{ id: string } | null>(
      `/admin/users/${userId}/demote-instance-admin`,
      {},
    ),
};
